/* eslint brace-style: ["error", "allman"] */
/**
 * Document lookup for pickers: find one document to act on (grant it to a team, attach it to an inspection) without
 * scanning the customer's Document table.
 *
 * A document is found either by its number (IDDocument, a primary-key read) or by name within one project (the
 * `IDProject, Deleted` index), never by an unanchored name LIKE across the whole customer: on LADOTD QA that read had
 * not answered after 150 s. Rows come from `Documents/LiteExtended/Name,DocumentType`, so FormData (megabytes per
 * record) never crosses the wire; Name and DocumentType were checked against the full record for document 4903130.
 *
 * Form type hashes (DocumentType, e.g. `LADOTD-ATM`) are shown by their form name (ReportNamedInstance.Name).
 */

const libPictSectionPicker = require('pict-section-picker');

const DEFAULT_PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 250;

/** The projection every lookup reads: the Lite columns plus the two a picker and a grant need. */
const DOCUMENT_COLUMNS = 'Name,DocumentType';

/**
 * @typedef {Object} DocumentLookupScope
 * @property {number} IDCustomer - The customer whose documents are searched.
 * @property {number} [IDProject] - The project a name search runs in; without one only a document number finds anything.
 * @property {number} [Start] - Record offset (default 0).
 * @property {number} [PageSize] - Records per request (default 25, never above 250).
 */

/**
 * @typedef {Object} DocumentPickerOption
 * @property {number} Value - IDDocument.
 * @property {string} Text - The document's name.
 * @property {string} Tag - The document number, shown as a badge.
 * @property {Record<string, any>} Record - The LiteExtended row (IDDocument, IDProject, Name, DocumentType, …).
 */

/**
 * @param {any} pValue
 * @returns {number} A positive integer, or 0.
 */
function positiveInteger(pValue)
{
	const tmpNumber = Number(pValue);
	return (Number.isInteger(tmpNumber) && (tmpNumber > 0)) ? tmpNumber : 0;
}

/**
 * @param {DocumentLookupScope} pScope
 * @returns {number} The requested page size, clamped to 1..250.
 */
function pageSize(pScope)
{
	const tmpSize = Math.floor(Number(pScope && pScope.PageSize) || DEFAULT_PAGE_SIZE);
	return Math.min(Math.max(tmpSize, 1), MAX_PAGE_SIZE);
}

/**
 * Tidy a search term: `~` is FoxHound's separator, and whitespace collapses.
 *
 * @param {any} pTerm - The raw search text.
 * @returns {string} The cleaned term ('' when nothing is left).
 */
function cleanTerm(pTerm)
{
	return String(pTerm == null ? '' : pTerm).replace(/~/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * The document number in a search term, when the term is one: `4903130`, `#4903130` or `No. 4903130`.
 *
 * @param {any} pTerm - The raw search text.
 * @returns {number} The IDDocument, or 0 when the term is not a document number.
 */
function parseDocumentNumber(pTerm)
{
	const tmpMatch = /^(?:no\.?\s*|#\s*)?(\d+)$/i.exec(cleanTerm(pTerm));
	return tmpMatch ? positiveInteger(tmpMatch[1]) : 0;
}

/**
 * The `Documents/LiteExtended` URL for a search, or '' when the search cannot be answered cheaply (no project and the
 * term is not a document number), in which case nothing should be requested.
 *
 * @param {any} pTerm - The raw search text.
 * @param {DocumentLookupScope} pScope
 * @returns {string} A URL relative to the API root, or ''.
 */
function buildDocumentSearchURL(pTerm, pScope)
{
	const tmpScope = pScope || { IDCustomer: 0 };
	const tmpIDCustomer = positiveInteger(tmpScope.IDCustomer);
	const tmpCustomerFilter = tmpIDCustomer ? `~FBV~IDCustomer~EQ~${tmpIDCustomer}` : '';
	const tmpBase = `Documents/LiteExtended/${DOCUMENT_COLUMNS}/FilteredTo`;
	const tmpNumber = parseDocumentNumber(pTerm);
	if (tmpNumber)
	{
		return `${tmpBase}/FBV~IDDocument~EQ~${tmpNumber}${tmpCustomerFilter}~FBV~Deleted~EQ~0/0/1`;
	}
	const tmpIDProject = positiveInteger(tmpScope.IDProject);
	if (!tmpIDProject)
	{
		return '';
	}
	const tmpNameFilter = libPictSectionPicker.prototype.buildSearchFilter.call(null, [ 'Name' ], cleanTerm(pTerm));
	const tmpFilter = [ `FBV~IDProject~EQ~${tmpIDProject}`, tmpNameFilter ].filter(Boolean).join('~');
	const tmpStart = Math.max(Math.floor(Number(tmpScope.Start) || 0), 0);
	return `${tmpBase}/${tmpFilter}${tmpCustomerFilter}~FBV~Deleted~EQ~0~FSF~IDDocument~DESC~0/${tmpStart}/${pageSize(tmpScope)}`;
}

/**
 * What an empty document list says, so the admin knows how to find a document.
 *
 * @param {any} pTerm - The raw search text.
 * @param {number} [pIDProject] - The selected project, if any.
 * @returns {string} The empty-list text.
 */
function documentSearchEmptyLabel(pTerm, pIDProject)
{
	if (parseDocumentNumber(pTerm))
	{
		return 'No document with that number';
	}
	if (!positiveInteger(pIDProject))
	{
		return 'Pick a project first, or type a document number';
	}
	return cleanTerm(pTerm) ? 'No document in this project matches that name' : 'This project has no documents';
}

/**
 * @param {Record<string, any>} pRow - A Document (or LiteExtended) row.
 * @returns {DocumentPickerOption} The picker option.
 */
function documentOption(pRow)
{
	const tmpID = positiveInteger(pRow && pRow.IDDocument);
	return { Value: tmpID, Text: (pRow && pRow.Name) || `Document #${tmpID}`, Tag: `#${tmpID}`, Record: pRow };
}

/**
 * A pict-section-picker DataProvider over the document lookup: `(term, page) => { results, hasMore }`. The scope is
 * read at call time, so a project picked after the picker mounted applies to the next search.
 *
 * @param {(pURL: string) => Promise<any>} fGetJSON - The caller's API GET (relative URL → parsed body).
 * @param {() => DocumentLookupScope} fScope - The current customer and project.
 * @returns {(pTerm: any, pPage: number) => Promise<{ results: Array<DocumentPickerOption>, hasMore: boolean }>}
 */
function documentPickerDataProvider(fGetJSON, fScope)
{
	return (pTerm, pPage) =>
	{
		const tmpScope = Object.assign({}, fScope());
		const tmpPageSize = pageSize(tmpScope);
		tmpScope.Start = (Number(pPage) || 0) * tmpPageSize;
		const tmpURL = buildDocumentSearchURL(pTerm, tmpScope);
		if (!tmpURL)
		{
			return Promise.resolve({ results: [], hasMore: false });
		}
		return Promise.resolve(fGetJSON(tmpURL))
			.then((pRows) =>
			{
				const tmpRows = Array.isArray(pRows) ? pRows.filter((pRow) => positiveInteger(pRow && pRow.IDDocument)) : [];
				return { results: tmpRows.map(documentOption), hasMore: (tmpRows.length >= tmpPageSize) && !parseDocumentNumber(pTerm) };
			})
			.catch(() => ({ results: [], hasMore: false }));
	};
}

/**
 * A pict-section-picker DataProvider over the customer's projects by name (Lite rows: the name is `Value`), in name
 * order. The API scopes Projects to the caller's customer.
 *
 * @param {(pURL: string) => Promise<any>} fGetJSON - The caller's API GET.
 * @returns {(pTerm: any, pPage: number) => Promise<{ results: Array<{ Value: number, Text: string, Record: Record<string, any> }>, hasMore: boolean }>}
 */
function projectPickerDataProvider(fGetJSON)
{
	return (pTerm, pPage) =>
	{
		const tmpNameFilter = libPictSectionPicker.prototype.buildSearchFilter.call(null, [ 'Name' ], cleanTerm(pTerm));
		const tmpFilter = [ tmpNameFilter, 'FBV~Deleted~EQ~0~FSF~Name~ASC~0' ].filter(Boolean).join('~');
		const tmpStart = (Number(pPage) || 0) * DEFAULT_PAGE_SIZE;
		return Promise.resolve(fGetJSON(`Projects/Lite/FilteredTo/${tmpFilter}/${tmpStart}/${DEFAULT_PAGE_SIZE}`))
			.then((pRows) =>
			{
				const tmpRows = Array.isArray(pRows) ? pRows.filter((pRow) => positiveInteger(pRow && pRow.IDProject)) : [];
				return {
					results: tmpRows.map((pRow) => ({ Value: Number(pRow.IDProject), Text: pRow.Value || pRow.Name || `Project #${pRow.IDProject}`, Record: pRow })),
					hasMore: (tmpRows.length >= DEFAULT_PAGE_SIZE),
				};
			})
			.catch(() => ({ results: [], hasMore: false }));
	};
}

/**
 * The URL resolving document type hashes to form names (the customer's own form types, then the shared ones).
 *
 * @param {Array<string>} pTypeHashes - DocumentType values.
 * @param {number} pIDCustomer - The customer.
 * @returns {string} A URL relative to the API root, or '' when there is nothing to resolve.
 */
function buildFormTypeNamesURL(pTypeHashes, pIDCustomer)
{
	const tmpHashes = Array.from(new Set((Array.isArray(pTypeHashes) ? pTypeHashes : []).map((pHash) => cleanTerm(pHash)).filter((pHash) => pHash && (pHash.indexOf(',') < 0))));
	if (!tmpHashes.length)
	{
		return '';
	}
	const tmpCustomers = [ positiveInteger(pIDCustomer), 0 ].filter((pID, pIndex, pAll) => pAll.indexOf(pID) === pIndex).join(',');
	return `ReportNamedInstances/LiteExtended/Hash,Name/FilteredTo/FBL~Hash~INN~${tmpHashes.map(encodeURIComponent).join(',')}~FBL~IDCustomer~INN~${tmpCustomers}~FBV~Deleted~EQ~0/0/${Math.min(tmpHashes.length * 4, MAX_PAGE_SIZE)}`;
}

/**
 * Form names by type hash. A customer's own form type wins over a shared one with the same hash.
 *
 * @param {Array<Record<string, any>>} pRows - ReportNamedInstance rows (Hash, Name, IDCustomer).
 * @param {number} pIDCustomer - The customer.
 * @returns {Record<string, string>} Hash → form name.
 */
function formTypeNamesByHash(pRows, pIDCustomer)
{
	/** @type {Record<string, string>} */
	const tmpNames = {};
	/** @type {Record<string, boolean>} */
	const tmpOwn = {};
	(Array.isArray(pRows) ? pRows : []).forEach((pRow) =>
	{
		const tmpName = pRow && (pRow.Name || pRow.Value);
		if (!pRow || !pRow.Hash || !tmpName)
		{
			return;
		}
		const tmpIsOwn = (positiveInteger(pRow.IDCustomer) === positiveInteger(pIDCustomer));
		if (!tmpNames[pRow.Hash] || (tmpIsOwn && !tmpOwn[pRow.Hash]))
		{
			tmpNames[pRow.Hash] = String(tmpName).trim();
			tmpOwn[pRow.Hash] = tmpIsOwn;
		}
	});
	return tmpNames;
}

module.exports =
{
	MAX_PAGE_SIZE,
	parseDocumentNumber,
	buildDocumentSearchURL,
	documentSearchEmptyLabel,
	documentOption,
	documentPickerDataProvider,
	projectPickerDataProvider,
	buildFormTypeNamesURL,
	formTypeNamesByHash,
};
